import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';

const PageContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(8),
}));

const ContentSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
}));

const Title = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(4),
  textAlign: 'center',
  fontWeight: 600,
}));

const ContentText = styled(Typography)(({ theme, ismyanmar }) => ({
  marginBottom: theme.spacing(3),
  lineHeight: ismyanmar ? 2 : 1.8,
  textAlign: 'justify',
  fontSize: ismyanmar ? '0.9rem' : '1rem',
}));

const ListItem = styled(Box)(({ theme, ismyanmar }) => ({
  marginBottom: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '0.5em',
    width: '6px',
    height: '6px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '50%',
  },
}));

const VisionAim = ({ language = 'mm' }) => {
  return (
    <PageContainer maxWidth="lg">
      <Title variant="h4">
        {language === 'en' ? 'Vision and Aim' : 'အတွေးအခေါ်နှင့် ရည်မှန်းချက်'}
      </Title>

      <ContentSection elevation={0}>
        {/* Myanmar Content */}
        {language === 'mm' && (
          <Box>
            <ContentText ismyanmar="true">
              "အနာဂတေ လဇ္ဇီ ရက္ခိဿတိ၊ လဇ္ဇီ ရက္ခိဿတိ၊ လဇ္ဇီ ရက္ခိဿတိ (အနာဂတ်သာသနာတော်ကို သီလဝန္တလဇ္ဇီ ရဟန်းတော်တို့ကသာ စောင့်ရှောက်ကြလိမ့်မည်)"ဟူသော ရှေးရဟန္တာ မထေရ်မြတ်တို့ ကြွေးကြော်တော်မူချက်ကို ဦးထိပ်တင် ခံယူကြ၍ အဖက်ဖက်က ယိုယွင်းလာနေသည့် သာသနာတော်မြတ်ကို ဝိနည်းပဋိပတ် သုတ္တန်ပဋိပတ်မပါဘဲ စာပေသင်ကြားပို့ချနေရုံမျှဖြင့် မလုံလောက်တော့သောကြောင့် 'အကျင့်လည်းပြင် စာလည်းသင် မြှင့်တင်သာသနာ"ဟူသော မူဝါဒကို ချမှတ်လျက် ဦးတည်လျှောက်လှမ်းနေသော တောရစာသင်တိုက် တစ်ခု ဖြစ်ပါသည်။
            </ContentText>

            <ContentText ismyanmar="true">
              အကျင့်လည်းပြင်-ဟူသည် သီလကို ဆိုလိုရင်းဖြစ်ပြီး စာလည်းသင်ဟူသည် ပညာကို ဆိုလိုရင်းဖြစ်သည်။ ထို့ကြောင့် ဗုဒ္ဓသာသနာတော်၏ ပုံရိပ်ကို မြှင့်တင်ရန် ဝိနည်းတော်ကို ထက်ဝန်းကျင်ပြည့်စုံစါာ လိုက်နာကျင့်သုံးသူ သီလဝန္တ သိက္ခာကာမရဟန်းသာမဏေများ ပညာသင်ကြားရာ တောရစာသင်တိုက်တစ်ခုအဖြစ် ဤကျောင်းတိုက်ကို ဖွဲ့စည်းထားပါသည်။
            </ContentText>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" color="primary.main" gutterBottom ismyanmar="true">
                ဤကျောင်းတိုက်၌ ကြာရှည်စွာ နေထိုက်သူများ
              </Typography>
              <Box sx={{ mt: 2 }}>
                <ListItem ismyanmar="true">
                  <Typography ismyanmar="true">
                    မိမိဆိုင်ရာ ကျောင်းတိုက်၌ စာချနေလိုသော ပုဂ္ဂိုလ်
                  </Typography>
                </ListItem>
                <ListItem ismyanmar="true">
                  <Typography ismyanmar="true">
                    ကျေးရွာကျောင်း၌ နေ၍ လူကြီး လူငယ်များ၏ စာရိတ္တနှင့် ပညာရေးကို အားပေးလိုသော ပုဂ္ဂိုလ်
                  </Typography>
                </ListItem>
                <ListItem ismyanmar="true">
                  <Typography ismyanmar="true">
                    မိမိအတွက်သာ တရားကျင့်၍ ဆိတ်ငြိမ်စွာ နေထိုင်လိုသော ပုဂ္ဂိုလ်
                  </Typography>
                </ListItem>
              </Box>
            </Box>
          </Box>
        )}

        {/* English Content */}
        {language === 'en' && (
          <Box>
            <ContentText>
              With reverence to the proclamation made by the ancient venerable monks, which states, 'In the future, the Sasana (Buddhist Order) will be preserved only by virtuous and modest monks,' this monastery is striving to uphold and advance the Sasana, which is deteriorating in various aspects. Recognizing that merely teaching and learning literature without adhering to the Vinaya (monastic discipline) and the Suttas (discourses) is no longer sufficient, the monastery has adopted the policy of 'Reforming conduct and teaching knowledge to elevate the Sasana' and is moving forward with this aim.
            </ContentText>

            <ContentText>
              The phrase 'Reforming conduct' refers to the practice of morality (Sila), while 'teaching knowledge' refers to education. Therefore, to elevate the image of the Buddhist Sasana, this monastery has been established as a forest monastery where virtuous monks and novices, who strictly observe the Vinaya and maintain their discipline, can pursue education.
            </ContentText>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" color="primary.main" gutterBottom>
                This monastery is suitable for the following individuals to reside for an extended period:
              </Typography>
              <Box sx={{ mt: 2 }}>
                <ListItem>
                  <Typography>
                    Those who wish to study and teach at their respective monasteries.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography>
                    Those who wish to reside in village schools and contribute to the moral and educational development of both adults and youth.
                  </Typography>
                </ListItem>
                <ListItem>
                  <Typography>
                    Those who wish to practice meditation and live peacefully for their own spiritual development.
                  </Typography>
                </ListItem>
              </Box>
            </Box>
          </Box>
        )}
      </ContentSection>
    </PageContainer>
  );
};

export default VisionAim; 